
import React, { useState, useEffect, useRef } from 'react';

const Flappy: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);

  useEffect(() => {
    if (!gameStarted || gameOver) return;
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext('2d')!;
    
    // HD DPI Scaling
    const dpr = window.devicePixelRatio || 1;
    const width = 400;
    const height = 500;
    
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    
    ctx.scale(dpr, dpr);

    let birdY = height / 2;
    let velocity = 0;
    const gravity = 0.25;
    const jump = -5.0;
    const birdSize = 24;

    let pipes: { x: number, top: number, bottom: number }[] = [];
    const pipeWidth = 60;
    const gap = 140;
    let frame = 0;

    const handleClick = (e: any) => { 
      e.preventDefault();
      velocity = jump; 
    };
    const handleKey = (e: KeyboardEvent) => { if(e.code === 'Space') { e.preventDefault(); velocity = jump; } };
    
    window.addEventListener('keydown', handleKey);
    canvas.addEventListener('mousedown', handleClick);
    canvas.addEventListener('touchstart', handleClick, { passive: false });

    const loop = () => {
      velocity += gravity;
      birdY += velocity;

      if (frame % 90 === 0) {
        const top = Math.random() * (height - gap - 100) + 50;
        pipes.push({ x: width, top, bottom: height - top - gap });
      }

      ctx.clearRect(0, 0, width, height);
      
      // Draw Bird (HD Style)
      ctx.shadowBlur = 15;
      ctx.shadowColor = '#38bdf8';
      ctx.fillStyle = '#38bdf8';
      ctx.beginPath();
      ctx.roundRect(50, birdY, birdSize, birdSize, 6);
      ctx.fill();

      // Draw Pipes
      ctx.shadowBlur = 0;
      ctx.fillStyle = '#1e293b';
      pipes.forEach((p, i) => {
        p.x -= 3;
        // Top pipe
        ctx.beginPath();
        ctx.roundRect(p.x, 0, pipeWidth, p.top, [0, 0, 8, 8]);
        ctx.fill();
        // Bottom pipe
        ctx.beginPath();
        ctx.roundRect(p.x, height - p.bottom, pipeWidth, p.bottom, [8, 8, 0, 0]);
        ctx.fill();
        
        // Collision
        if (50 + birdSize > p.x && 50 < p.x + pipeWidth) {
          if (birdY < p.top || birdY + birdSize > height - p.bottom) setGameOver(true);
        }
        if (p.x === 50) setScore(s => s + 1);
      });

      if (birdY > height || birdY < 0) setGameOver(true);
      pipes = pipes.filter(p => p.x > -pipeWidth);

      frame++;
      if (!gameOver) requestAnimationFrame(loop);
    };

    const animId = requestAnimationFrame(loop);
    return () => {
      window.removeEventListener('keydown', handleKey);
      canvas.removeEventListener('mousedown', handleClick);
      canvas.removeEventListener('touchstart', handleClick);
      cancelAnimationFrame(animId);
    };
  }, [gameStarted, gameOver]);

  return (
    <div className="flex flex-col items-center w-full max-w-full">
      <div className="mb-6 flex flex-row justify-between w-full max-w-[400px] items-center glass p-4 rounded-2xl">
        <span className="text-[10px] font-black text-sky-400 tracking-widest uppercase">UPTIME MONITOR</span>
        <span className="text-2xl font-black font-mono text-white">{score}s</span>
      </div>
      
      <div className="relative group overflow-hidden rounded-3xl">
        <div className="absolute -inset-1 bg-sky-500/20 rounded-3xl blur-xl opacity-50"></div>
        <canvas 
          ref={canvasRef} 
          className="relative bg-slate-900 border-4 border-slate-800 rounded-3xl shadow-2xl cursor-pointer" 
        />
      </div>

      <div className="mt-8 flex flex-col items-center gap-4">
        {(!gameStarted || gameOver) && (
          <button 
            onClick={() => { setGameOver(false); setGameStarted(true); setScore(0); }} 
            className="bg-sky-600 hover:bg-sky-500 text-white px-12 py-4 rounded-2xl font-black shadow-xl shadow-sky-500/20 transition-all active:scale-95 uppercase tracking-widest"
          >
            {gameOver ? 'REBOOT SESSION' : 'INITIALIZE SYSTEM'}
          </button>
        )}
        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest text-center">
          Tap or Space to Jump
        </p>
      </div>
    </div>
  );
};

export default Flappy;
